import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Row, FormControl, Button } from 'react-bootstrap';
import moment from 'moment';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import withForm from '../../hoc/withForm';
import { Multiselect } from 'multiselect-react-dropdown';

import { useReportCustomEmailProviderStore } from '../../store/providers/ReportCustomEmail';
import { useFranchiseStore } from '../../store/providers/Franchise';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import { isEmpty, orderBy } from 'lodash';

import Spinner from '../../components/Spinner/Spinner';
import Permission from '../../components/Permission/Permission';

import validations from './validations';
import './sidesVolumeExport.css';

import * as constants from '../../constants';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const InputCollection = React.lazy(() => import('../../components/InputCollection/InputCollection'));
const InformationModal = React.lazy(() => import('../../components/Modals/InformationModal/InformationModal'));

function SidesVolumeExport(props) {
    const dispatch = useDispatch();

    const { listRecords, data: franchiseData } = useFranchiseStore();
    const { createRecordSidesVolume } = useReportCustomEmailProviderStore();
    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [showInformationModal, setShowInformationModal] = useState(false);

    const pagePermission = constants.PERMISSION_REPORTS_SIDESVOLUMEEXPORT;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const [franchiseOptions, setFranchiseOptions] = useState([]);
    const [selectedFranchises, setSelectedFranchises] = useState([]);
    const franchiseDropdown = useRef(null);

    const submittedHandler = (e) => {
        e.preventDefault();

        let franchiseIds = [];
        franchiseIds = selectedFranchises.map((selectedFranchise) => {
            return selectedFranchise.id;
        })
        props.formData.franchiseIds = franchiseIds;

        var valid = props.formValidate(validations);
        if (valid) {
            props.formSubmitted();
            setShowInformationModal(true);
            createRecordSidesVolume(props.formData);
        }
    };

    const infoClickedHandler = () => {
        setShowInformationModal(false);
    };

    useEffect(() => {
        if (!isEmpty(franchiseData)) {
            if (franchiseData.scope === 'list'
                && franchiseData.status === 'success'
            ) {
                let options = [];
                if (!isEmpty(franchiseData)
                    && franchiseData.scope === 'list'
                    && franchiseData.status === 'success') {
                    const options = franchiseData.body.map((franchise) => {
                        return { id: franchise.id, name: `${franchise.name} (${franchise.instance})` }
                    });

                    setFranchiseOptions(options);
                }

                franchiseData.body.forEach((franchise) => {
                    if (franchise.demo) { return; }

                    let include = false;

                    if (isEmpty(franchise.terminatedOn)) { include = true; }

                    if (!include) {
                        const now = moment(new Date());
                        const days30 = moment(now).subtract(30, 'days');

                        if (moment(franchise.terminatedOn) > days30) { include = true; }
                    }

                    if (include) {
                        options.push({ id: franchise.id, name: `${franchise.instance.toUpperCase()}` });
                    }
                });

                options = orderBy(options, ['name']);
                options = [
                    { id: 0, name: 'All Instances' },
                    { id: -1, name: 'Corporate' },
                    { id: -2, name: 'Non-Corporate' }].concat(options);
                    
                setFranchiseOptions(options);
            }

            setIsBusy(franchiseData.status === 'processing' && franchiseData.scope === 'list');
        }

    }, [franchiseData, props]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'sidesVolumeExport', text: 'Sides & Volume Export', path: window.location.pathname }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    const selectHandler = (list, item) => {
        setSelectedFranchises(list);

        let franchiseIds = [];
        list.forEach((franchise) => franchiseIds.push(franchise.id));

        props.formChanged('franchiseIds', (isEmpty(franchiseIds) ? [] : franchiseIds.join(',')));
    };

    const removeHandler = (list, _item) => {
        setSelectedFranchises(list);

        let franchiseIds = [];

        list.forEach((franchise) => franchiseIds.push(franchise.id));

        props.formChanged('franchiseIds', (isEmpty(franchiseIds) ? [] : franchiseIds.join(',')));
    };

    return (
        <>
            <SideMenu activeKey="reports" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && 
                    <>
                        {
                            (!isMounted || isBusy) && <Spinner text="Loading Sides & Volume Export..." />
                        }
                        {
                            showInformationModal && <InformationModal
                                show={showInformationModal}
                                clicked={infoClickedHandler}
                                title="Sides & Volume Export Submitted"
                                buttonText='OK! Got it!'>
                                <div>
                                    Sides & Volume Export will take a little time to run.  Your request is currently being processed and will be emailed to you within the next few minutes.
                                </div>
                            </InformationModal>
                        }

                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Sides & Volume Export" />
                            {
                                isMounted && (
                                    <Form onSubmit={(e) => submittedHandler(e)}>
                                        <Row>
                                            <div className="col-md-4">
                                                <InputCollection required labelText="Instances" errorMessage={props.formValidationCheck('franchiseIds')}>
                                                    <Multiselect
                                                        options={franchiseOptions}
                                                        displayValue="name"
                                                        disabled={isBusy}
                                                        placeholder="Select Instances..."
                                                        emptyRecordMsg="No Instances Available"
                                                        selectedValues={selectedFranchises}
                                                        ref={franchiseDropdown}
                                                        avoidHighlightFirstOption={true}
                                                        closeOnSelect={false}
                                                        onSelect={(list, item) => selectHandler(list, item)}
                                                        onRemove={(list, item) => removeHandler(list, item)}
                                                    />
                                                </InputCollection>
                                            </div>
                                            <div className="col-md-4">
                                                <InputCollection labelText="Closing Start Date" errorMessage={props.formValidationCheck('beginDate')}>
                                                    <FormControl id="beginDate"
                                                        type="date"
                                                        disabled={isBusy}
                                                        maxLength={10}
                                                        onChange={(e) => props.formChanged(e.target.id, e.target.value)} 
                                                        defaultValue={props.formData['beginDate'] || ''}>
                                                    </FormControl>      
                                                </InputCollection>                      
                                            </div>
                                            <div className="col-md-4">
                                                <InputCollection labelText="Closing End Date" errorMessage={props.formValidationCheck('endDate')}>
                                                    <FormControl id="endDate"
                                                        type="date"
                                                        disabled={isBusy}
                                                        maxLength={10}
                                                        onChange={(e) => props.formChanged(e.target.id, e.target.value)} 
                                                        defaultValue={props.formData['endDate'] || ''}>
                                                    </FormControl>      
                                                </InputCollection>                      
                                            </div>
                                        </Row>
                                        <Row>
                                            <div className="col-md-12">
                                                <hr className="form-divider" />
                                                <div className="form-action-buttons">
                                                    <Button bsStyle="danger" variant="danger" type="submit" className="button-group-margin" disabled={!canEdit || isBusy}>
                                                        <i className="fa fa-play button-icon"></i>
                                                        { isBusy ? 'Busy...' : 'Run' }
                                                    </Button>
                                                </div>
                                            </div>
                                        </Row>
                                    </Form>
                                )
                            }
                        </div>
                    </>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(withForm(SidesVolumeExport)));